import { VolumeSlider } from "@vidstack/react";
import { Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getPlayerActions } from "@/lib/player/player.store";

interface VolumeControlProps {
	isMuted: boolean;
	volumePercent: number;
}
/**
 * VolumeControl Component
 *
 * Volume control with slider and mute button.
 * Displays current volume percentage and allows adjustment.
 */

export function VolumeControl({ isMuted, volumePercent }: VolumeControlProps) {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="h-9 w-9 text-white hover:text-primary hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-150 rounded-full"
					aria-label="Volume"
				>
					{isMuted || volumePercent === 0 ? (
						<VolumeX className="h-7 w-7" />
					) : (
						<Volume2 className="h-7 w-7" />
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-40 p-4">
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<p className="text-xs font-medium text-muted-foreground">Volume</p>
						<span className="text-xs font-semibold">
							{isMuted ? 0 : Math.round(volumePercent)}%
						</span>
					</div>
					<VolumeSlider.Root aria-label="Volume" className="h-6 w-full">
						<VolumeSlider.Track className="relative mx-auto h-1 w-full rounded-full bg-muted">
							<VolumeSlider.TrackFill
								className="absolute left-0 top-0 h-full rounded-full bg-primary/80"
								style={{ width: "var(--slider-fill, 0%)" }}
							/>
							<VolumeSlider.Thumb
								className="absolute top-1/2 size-2 -translate-y-1/2 -translate-x-1/2 rounded-full bg-primary"
								style={{ left: "var(--slider-fill, 0%)" }}
							/>
						</VolumeSlider.Track>
					</VolumeSlider.Root>
					<Button
						variant="outline"
						size="sm"
						className="w-full bg-transparent"
						onClick={getPlayerActions().toggleMute}
					>
						{isMuted ? "Unmute" : "Mute"}
					</Button>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
